import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useAwardingApi from './useAwardingApi';
import {
  unwrapAwardingCollection,
  unwrapAwardingDetail,
} from '../utils/awardingHelpers';

const useAwardingDetail = (id, viewerType = 1) => {
  const { getAwardingById, getActivities, getResponses } = useAwardingApi();
  const [awarding, setAwarding] = useState(null);
  const [activities, setActivities] = useState([]);
  const [responses, setResponses] = useState([]);
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);
  const [isLoadingResponses, setIsLoadingResponses] = useState(false);

  const fetchAwarding = useCallback(async () => {
    if (!id) return;

    setIsLoading(true);
    try {
      const result = await getAwardingById(id);
      setAwarding(unwrapAwardingDetail(result));
    } catch (error) {
      toast.error(error.message || 'Failed to load awarding detail');
      setAwarding(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, getAwardingById]);

  const fetchActivities = useCallback(async () => {
    if (!id) return;

    setIsLoadingActivities(true);
    try {
      const result = await getActivities(id);
      setActivities(unwrapAwardingCollection(result));
    } catch (error) {
      toast.error(error.message || 'Failed to load awarding activity');
      setActivities([]);
    } finally {
      setIsLoadingActivities(false);
    }
  }, [id, getActivities]);

  const fetchResponses = useCallback(async () => {
    if (!id) return;

    setIsLoadingResponses(true);
    try {
      const result = await getResponses(id, viewerType);
      setResponses(unwrapAwardingCollection(result));
    } catch (error) {
      toast.error(error.message || 'Failed to load awarding response');
      setResponses([]);
    } finally {
      setIsLoadingResponses(false);
    }
  }, [id, viewerType, getResponses]);

  useEffect(() => {
    fetchAwarding();
  }, [fetchAwarding]);

  useEffect(() => {
    if (awarding && awarding.status !== 0) {
      fetchResponses();
    }
  }, [awarding, fetchResponses]);

  return {
    awarding,
    activities,
    responses,
    isLoading,
    isLoadingActivities,
    isLoadingResponses,
    refresh: fetchAwarding,
    refreshActivities: fetchActivities,
    refreshResponses: fetchResponses,
  };
};

export default useAwardingDetail;
